import { LiveTvRounded } from '@mui/icons-material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import { Alert, Box, Button, ButtonBase, CircularProgress, IconButton, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { AUTHORITIES } from 'app/config/constants';
import { useAppSelector } from 'app/config/store';
import {
  HatJsVideoPlayer,
  HatJsVideoPlayerHandle,
  HatJsVideoQuality,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-cameras-tab/HatJsVideoPlayer';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import SelectPaginateFromPaginatedApi from 'app/shared/components/select-paginate/SelectPaginateFromPaginatedApi';
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface VehicleOption {
  id: number;
  licensePlate?: string;
  deviceStatus?: number | null;
  terminalID?: string;
}

interface CompanyOption {
  id: number;
  name?: string;
}

interface CameraChannel {
  id: number;
  channelNumber: number;
  displayName?: string;
}

interface CameraLiveStream {
  channelId: number;
  url: string;
  contentType: string;
}

const PLAYER_ASPECT_RATIO = 16 / 9;
const PLAYER_BORDER_SIZE = 2;
const VEHICLE_API_URL = 'api/admin/camera-devices/options';

const APP_BAR_OFFSET = { desktop: 64, mobile: 56 };
const SIDEBAR_PADDING = 2;

const channelLabel = (channel: CameraChannel) => channel.displayName || `Câmera ${channel.channelNumber}`;

const GENERIC_CAMERA_ERROR = 'Não foi possível conectar à câmera. Tente novamente mais tarde.';

const messageFromError = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) return fallback;

  const responseDetail = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : '';
  const responseMessage = typeof error.response?.data?.message === 'string' ? error.response.data.message : '';
  const candidate = responseDetail || responseMessage;
  const isTechnicalMessage = /request failed with status code|network error|timeout|hat cloud|error\.http\./i.test(candidate || error.message);

  return candidate && !isTechnicalMessage ? candidate : GENERIC_CAMERA_ERROR;
};

const vehicleStatusLabel = (status?: number | null) => {
  if (status === 1) return 'Online';
  if (status === 0) return 'Offline';
  if (status === 2) return 'Em repouso';
  return 'Desconhecido';
};

const vehicleOptionLabel = (option?: VehicleOption | null) => {
  const licensePlate = option?.licensePlate || String(option?.id || '');
  const status = vehicleStatusLabel(option?.deviceStatus);
  return status ? `${licensePlate} · ${status}` : licensePlate;
};

const vehicleStatusChipSx = (status: number | null | undefined, theme: Theme) => {
  if (status === 1) {
    return { color: theme.palette.success.main, backgroundColor: alpha(theme.palette.success.main, 0.12) } as const;
  }
  if (status === 0) {
    return { color: theme.palette.text.secondary, backgroundColor: alpha(theme.palette.text.secondary, 0.12) } as const;
  }
  if (status === 2) {
    return { color: theme.palette.warning.main, backgroundColor: alpha(theme.palette.warning.main, 0.12) } as const;
  }
  return { color: theme.palette.text.secondary, backgroundColor: theme.palette.action.selected } as const;
};

const renderVehicleOption = (option: VehicleOption | null | undefined, theme: Theme) => {
  const status = vehicleStatusLabel(option?.deviceStatus);
  const chipStyle = vehicleStatusChipSx(option?.deviceStatus, theme);
  const licensePlate = option?.licensePlate || String(option?.id || '');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, width: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{licensePlate}</div>
      <div
        style={{
          ...chipStyle,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 22,
          minHeight: 22,
          minWidth: 58,
          padding: '0 8px',
          borderRadius: 12,
          fontSize: '0.6875rem',
          fontWeight: 600,
          lineHeight: '22px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {status}
      </div>
    </div>
  );
};

const getVideoColumnWidth = (layout: HTMLElement): number => {
  const layoutWidth = layout.getBoundingClientRect().width;
  const layoutStyle = window.getComputedStyle(layout);
  const gridColumns = layoutStyle.gridTemplateColumns.split(/\s+/);
  const sidebarWidth = Number.parseFloat(gridColumns[0] || '') || 360;
  const lastGridColumn = gridColumns[gridColumns.length - 1] || '';
  const computedContentWidth = lastGridColumn.endsWith('px') ? Number.parseFloat(lastGridColumn) : Number.NaN;
  const fallbackContentWidth = layoutWidth - sidebarWidth - (Number.parseFloat(layoutStyle.columnGap) || 0);
  return Number.isFinite(computedContentWidth) ? computedContentWidth : fallbackContentWidth;
};

const SectionLabel: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {hint}
      </Typography>
    )}
  </Stack>
);

export const LiveMonitor: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const fixedVehicleParams = new URLSearchParams(history.location.search);
  const fixedTruckImplementId = Number(fixedVehicleParams.get('truckImplementId')) || null;
  const fixedLicensePlate = fixedVehicleParams.get('licensePlate') || '';
  const fixedCompanyId = Number(fixedVehicleParams.get('companyId')) || null;
  const fixedCompanyName = fixedVehicleParams.get('companyName') || '';
  const fixedChannelIds = fixedVehicleParams.get('channelIds')?.split(',').map(Number).filter(Number.isFinite) ?? [];
  const [company, setCompany] = useState<CompanyOption | null>(null);
  const [vehicle, setVehicle] = useState<VehicleOption | null>(null);
  const [vehicleListRefreshKey, setVehicleListRefreshKey] = useState(0);
  const [refreshingVehicleList, setRefreshingVehicleList] = useState(false);
  const [channels, setChannels] = useState<CameraChannel[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);
  const [quality, setQuality] = useState<HatJsVideoQuality>('SUB');
  const [liveStreams, setLiveStreams] = useState<CameraLiveStream[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingLive, setLoadingLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playerMaxWidth, setPlayerMaxWidth] = useState<number | null>(null);
  const [playerMaxHeight, setPlayerMaxHeight] = useState<number | null>(null);
  const [isPlayerFullscreen, setIsPlayerFullscreen] = useState(false);
  const monitorLayoutRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLElement>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const livePlayerRef = useRef<HatJsVideoPlayerHandle>(null);
  const vehicleApiBaseUrl = isAdmin && company?.id ? `${VEHICLE_API_URL}?searchCompanyId=${company.id}` : VEHICLE_API_URL;
  const vehicleApiUrl = `${vehicleApiBaseUrl}${vehicleApiBaseUrl.includes('?') ? '&' : '?'}refreshKey=${vehicleListRefreshKey}`;
  const buildCameraNavigationQuery = () => {
    const params = new URLSearchParams();
    params.set('navigation', 'sidebar');
    if (vehicle?.id) params.set('truckImplementId', String(vehicle.id));
    if (vehicle?.licensePlate) params.set('licensePlate', vehicle.licensePlate);
    if (company?.id) params.set('companyId', String(company.id));
    if (company?.name) params.set('companyName', company.name);
    if (selectedChannelIds.length) params.set('channelIds', selectedChannelIds.join(','));
    return params.toString();
  };
  const fixedVehicleQuery = fixedTruckImplementId
    ? `truckImplementId=${fixedTruckImplementId}${fixedLicensePlate ? `&licensePlate=${encodeURIComponent(fixedLicensePlate)}` : ''}${
        fixedCompanyId ? `&companyId=${fixedCompanyId}` : ''
      }${fixedCompanyName ? `&companyName=${encodeURIComponent(fixedCompanyName)}` : ''}${
        fixedChannelIds.length ? `&channelIds=${fixedChannelIds.join(',')}` : ''
      }`
    : '';
  const playbackRoute = fixedVehicleQuery ? `/camera-playback?${fixedVehicleQuery}` : '/camera-playback';

  useEffect(() => {
    if (fixedCompanyId && !company) setCompany({ id: fixedCompanyId, name: fixedCompanyName || undefined });
  }, [company, fixedCompanyId, fixedCompanyName]);

  useEffect(() => {
    if (!fixedTruckImplementId) return;
    setVehicle(current =>
      current?.id === fixedTruckImplementId && current.licensePlate === fixedLicensePlate
        ? current
        : { id: fixedTruckImplementId, licensePlate: fixedLicensePlate || undefined }
    );
  }, [fixedLicensePlate, fixedTruckImplementId]);

  const refreshVehicleList = async () => {
    setRefreshingVehicleList(true);
    try {
      const { data } = await axios.get(vehicleApiBaseUrl, {
        params: {
          page: 0,
          size: 10,
          sort: 'licensePlate',
          searchLicensePlate: '',
          cacheBuster: new Date().getTime(),
        },
      });
      setVehicle(current => {
        if (!current) return current;
        const refreshedVehicle = (data.content || []).find((option: VehicleOption) => option.id === current.id);
        return refreshedVehicle || current;
      });
      setVehicleListRefreshKey(current => current + 1);
    } finally {
      setRefreshingVehicleList(false);
    }
  };

  useEffect(() => {
    if (!fixedTruckImplementId) return;
    void refreshVehicleList();
  }, [fixedTruckImplementId, vehicleApiBaseUrl]);

  useEffect(() => {
    const form = formRef.current;
    const layout = monitorLayoutRef.current;
    if (!form || !layout) return undefined;

    const updatePlayerBounds = () => {
      const formHeight = form.getBoundingClientRect().height;
      const availableWidth = getVideoColumnWidth(layout);
      const playerBounds = playerContainerRef.current?.getBoundingClientRect();
      const controlsHeight = playerBounds ? Math.max(playerBounds.height - playerBounds.width * PLAYER_ASPECT_RATIO, 0) : 0;
      const maxWidthByHeight = Math.max((formHeight - controlsHeight - PLAYER_BORDER_SIZE) * PLAYER_ASPECT_RATIO + PLAYER_BORDER_SIZE, 1);
      setPlayerMaxHeight(Math.max(formHeight, 1));
      setPlayerMaxWidth(Math.max(Math.min(availableWidth, maxWidthByHeight), 1));
    };

    updatePlayerBounds();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePlayerBounds);
      return () => window.removeEventListener('resize', updatePlayerBounds);
    }

    const observer = new ResizeObserver(updatePlayerBounds);
    observer.observe(form);
    observer.observe(layout);
    if (playerContainerRef.current) observer.observe(playerContainerRef.current);
    return () => observer.disconnect();
  }, [liveStreams.length]);

  useEffect(() => {
    let active = true;
    setChannels([]);
    setSelectedChannelIds([]);
    setLiveStreams([]);
    setError(null);

    if (!vehicle) return undefined;

    setLoadingChannels(true);
    void (async () => {
      try {
        const response = await axios.get<any>('api/admin/camera-devices', { params: { size: 1, truckImplementId: vehicle.id } });
        if (!active) return;

        const results = response.data.data?.results || response.data.results || [];
        const device = results.find((d: any) => d.truckImplementId === vehicle.id && d.deviceChannelList);

        if (device && device.deviceStatus !== 1) {
          setError(
            device.deviceStatus === 0
              ? 'Este veículo está offline no momento. Aguarde alguns instantes e tente novamente quando o equipamento estiver online.'
              : device.deviceStatus === 2
              ? 'A câmera deste veículo está em repouso. O acesso ao vivo estará disponível quando ela estiver online.'
              : 'Não foi possível confirmar o status da câmera. Tente novamente mais tarde.'
          );
          return;
        }

        if (device && device.deviceChannelList && Array.isArray(device.deviceChannelList)) {
          const disabledChannelNumbers: number[] = device.disabledChannelNumbers || [];
          const videoChannels = device.deviceChannelList.filter((ch: any) => ch.channelType === 0 && !disabledChannelNumbers.includes(Number(ch.channelNum)));

          const formattedChannels: CameraChannel[] = videoChannels.map((ch: any, idx: number) => ({
            id: Number(ch.channelNum) || idx + 1,
            channelNumber: Number(ch.channelNum) || idx + 1,
            displayName: ch.channelName || undefined,
          }));

          setChannels(formattedChannels);
          setSelectedChannelIds(
            fixedChannelIds.length ? formattedChannels.filter(channel => fixedChannelIds.includes(channel.id)).map(channel => channel.id) : []
          );
        } else {
          setError('Este veículo não possui câmeras vinculadas.');
        }
      } catch (requestError) {
        if (active) setError(messageFromError(requestError, 'Não foi possível carregar os canais da câmera.'));
      } finally {
        if (active) setLoadingChannels(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [vehicle]);

  const selectedChannels = useMemo(() => channels.filter(channel => selectedChannelIds.includes(channel.id)), [channels, selectedChannelIds]);

  const playerStreams = useMemo(
    () =>
      selectedChannels
        .map(channel => {
          const stream = liveStreams.find(candidate => candidate.channelId === channel.id);
          return stream ? { channelId: channel.id, title: channelLabel(channel), url: stream.url } : null;
        })
        .filter((stream): stream is { channelId: number; title: string; url: string } => stream !== null),
    [liveStreams, selectedChannels]
  );

  const isLiveActive = liveStreams.length > 0;

  const toggleChannel = (channelId: number) => {
    if (isLiveActive) return;
    setError(null);
    setLiveStreams([]);
    setSelectedChannelIds(current => {
      if (current.includes(channelId)) return current.filter(id => id !== channelId);
      return [...current, channelId];
    });
  };

  const fetchLiveStreams = async (requestedQuality: HatJsVideoQuality) => {
    if (!vehicle) {
      setError('Selecione um dispositivo antes de iniciar a live.');
      return null;
    }
    if (!selectedChannelIds.length) {
      setError('Selecione pelo menos uma câmera para iniciar a live.');
      return null;
    }

    const response = await axios.get<CameraLiveStream[]>(`api/cameras/truck-implements/${vehicle.id}/live`, {
      params: { channelIds: selectedChannelIds.join(','), quality: requestedQuality },
    });

    const returnedChannelIds = new Set(response.data.map(stream => stream.channelId));

    if (!response.data.length || selectedChannelIds.some(channelId => !returnedChannelIds.has(channelId))) {
      throw new Error('Não foi possível abrir o vídeo de todos os canais selecionados.');
    }
    return response.data;
  };

  const startLive = async () => {
    const initialQuality: HatJsVideoQuality = 'SUB';
    setError(null);
    setQuality(initialQuality);
    setLoadingLive(true);
    try {
      const streams = await fetchLiveStreams(initialQuality);
      if (streams) setLiveStreams(streams);
    } catch (requestError) {
      setError(messageFromError(requestError, 'Não foi possível iniciar a visualização ao vivo.'));
    } finally {
      setLoadingLive(false);
    }
  };

  const changeQuality = async (nextQuality: HatJsVideoQuality) => {
    if (nextQuality === quality) return;
    setQuality(nextQuality);
    if (!isLiveActive) return;

    setError(null);
    setLoadingLive(true);
    try {
      const streams = await fetchLiveStreams(nextQuality);
      if (streams) {
        const restarted = await livePlayerRef.current?.restartLive(
          streams.map(stream => {
            const channel = channels.find(candidate => candidate.id === stream.channelId);
            return { channelId: stream.channelId, title: channel ? channelLabel(channel) : `Câmera ${stream.channelId}`, url: stream.url };
          }),
          nextQuality
        );
        setLiveStreams(streams);
        if (restarted === false) {
          setError('Não foi possível aplicar a nova qualidade. Tente iniciar a transmissão novamente.');
        }
      }
    } catch (requestError) {
      setError(messageFromError(requestError, 'Não foi possível alterar a qualidade do vídeo.'));
    } finally {
      setLoadingLive(false);
    }
  };

  const stopLive = () => {
    setLiveStreams([]);
    setQuality('SUB');
    setError(null);
  };

  const canStartLive = Boolean(vehicle) && selectedChannelIds.length > 0 && !loadingLive;

  const startHint = (() => {
    if (loadingLive || isLiveActive) return null;
    if (isAdmin && !company?.id && !fixedTruckImplementId) return 'Selecione uma empresa para listar os veículos.';
    if (!vehicle) return 'Selecione um veículo para listar as câmeras.';
    if (loadingChannels) return 'Carregando os canais do veículo...';
    if (!channels.length) return 'Nenhuma câmera disponível para este veículo.';
    if (!selectedChannelIds.length) return 'Selecione ao menos uma câmera.';
    return null;
  })();

  const onVideoPrimary = theme.palette.text.primary;
  const onVideoSecondary = theme.palette.text.secondary;
  const onVideoDisabled = theme.palette.text.disabled;

  const togglePlayerFullscreen = () => {
    const playerElement = playerContainerRef.current;
    if (!playerElement) return;

    if (!isPlayerFullscreen) {
      if (playerElement.requestFullscreen) {
        void playerElement.requestFullscreen().catch(() => {
          setIsPlayerFullscreen(true);
        });
      } else if ((playerElement as any).webkitRequestFullscreen) {
        void (playerElement as any).webkitRequestFullscreen().catch(() => {
          setIsPlayerFullscreen(true);
        });
      } else {
        setIsPlayerFullscreen(true);
      }
    } else {
      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else if ((document as any).webkitFullscreenElement) {
        void (document as any).webkitExitFullscreen();
      }
      setIsPlayerFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = Boolean(document.fullscreenElement || (document as any).webkitFullscreenElement);
      setIsPlayerFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: 'primary.main',
        borderRadius: '8px',
        padding: '24px',
        margin: '24px',
      }}
    >
      <Box
        ref={monitorLayoutRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '360px minmax(0, 1fr)', xl: '360px minmax(0, 1fr)' },
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        <Box
          ref={formRef}
          component="aside"
          aria-label="Controles da transmissão"
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            height: { xs: 'auto', md: '72vh' },
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              px: SIDEBAR_PADDING,
              py: 1.25,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LiveTvRounded
                sx={{
                  fontSize: 20,
                  flexShrink: 0,
                  position: 'relative',
                  bottom: 2,
                }}
                color="success"
              />
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  m: 0,
                }}
              >
                Ao Vivo
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const query = buildCameraNavigationQuery();
                history.push(query ? `/camera-playback?${query}` : playbackRoute);
              }}
              sx={{
                padding: '4px 12px',
                fontSize: '0.75rem',
                textTransform: 'none',
                borderRadius: '8px',
                borderColor: theme.palette.divider,
                backgroundColor: 'background.default',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  borderColor: theme.palette.secondary.main,
                  color: 'secondary.main',
                },
              }}
            >
              Ir para Playback
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: SIDEBAR_PADDING,
              py: 1.5,
            }}
          >
            <Stack spacing={2}>
              {error && (
                <Alert severity="error" variant="outlined" onClose={() => setError(null)} sx={{ borderRadius: '8px' }}>
                  {error}
                </Alert>
              )}

              <Stack spacing={1.25} divider={<Box sx={{ borderTop: '1px solid', borderColor: alpha(theme.palette.text.primary, 0.06) }} />}>
                <Stack spacing={1.25}>
                  {isAdmin && (
                    <Box>
                      <SectionLabel title="Empresa" />
                      <SelectPaginate
                        apiUrl="api/admin/camera-devices/companies/options"
                        value={company}
                        valueName="name"
                        searchName="search"
                        placeholder="Buscar por empresa"
                        menuPortalTarget={document.body}
                        controlHeight={36}
                        controlFontSize="0.875rem"
                        isRequired
                        displayFormat={option => option?.name || String(option?.id || '')}
                        onChange={value => {
                          setCompany(value as CompanyOption | null);
                          setVehicle(null);
                        }}
                      />
                    </Box>
                  )}
                  {
                    <Box>
                      <SectionLabel title="Veículo" />
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 36px', columnGap: 0.75, alignItems: 'center' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <SelectPaginateFromPaginatedApi
                            apiUrl={vehicleApiUrl}
                            value={vehicle}
                            valueName="licensePlate"
                            searchName="searchLicensePlate"
                            placeholder="Buscar por placa"
                            menuPortalTarget={document.body}
                            controlHeight={36}
                            controlFontSize="0.875rem"
                            isRequired
                            isDisabled={isAdmin && !company?.id && !fixedTruckImplementId}
                            displayFormat={vehicleOptionLabel}
                            renderOption={option => renderVehicleOption(option, theme)}
                            minWidth="0"
                            desktopMinWidth="0"
                            onChange={value => setVehicle(value as VehicleOption | null)}
                          />
                        </Box>
                        <Tooltip title="Atualizar veículos e status">
                          <span>
                            <IconButton
                              size="small"
                              color="secondary"
                              disabled={(isAdmin && !company?.id && !fixedTruckImplementId) || refreshingVehicleList}
                              onClick={refreshVehicleList}
                              sx={{ border: '1px solid', borderColor: alpha(theme.palette.text.primary, 0.18), borderRadius: '8px', height: 36, width: 36 }}
                            >
                              {refreshingVehicleList ? <CircularProgress color="secondary" size={16} /> : <SyncRoundedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  }
                </Stack>

                <Box>
                  <SectionLabel title="Câmeras" hint={channels.length > 0 ? `${selectedChannelIds.length}/${channels.length}` : undefined} />

                  {loadingChannels ? (
                    <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
                      <CircularProgress size={24} color="secondary" />
                      <Typography variant="caption" color="text.secondary">
                        Carregando câmeras
                      </Typography>
                    </Stack>
                  ) : channels.length > 0 ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                      {channels.map(channel => {
                        const isSelected = selectedChannelIds.includes(channel.id);
                        const isChannelDisabled = isLiveActive;
                        return (
                          <ButtonBase
                            key={channel.id}
                            onClick={() => toggleChannel(channel.id)}
                            disabled={isChannelDisabled}
                            aria-pressed={isSelected}
                            aria-label={channelLabel(channel)}
                            sx={{
                              height: 32,
                              minHeight: 32,
                              px: 1,
                              gap: 0.625,
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              borderRadius: '8px',
                              border: '1px solid',
                              borderColor: isSelected ? alpha(theme.palette.secondary.main, 0.4) : alpha(theme.palette.text.primary, 0.12),
                              backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.08) : 'background.default',
                              color: isSelected ? 'text.primary' : 'text.secondary',
                              opacity: isChannelDisabled ? 0.45 : 1,
                              transition: theme.transitions.create(['background-color', 'border-color', 'color', 'opacity']),
                              '&:hover': {
                                borderColor: isSelected ? alpha(theme.palette.secondary.main, 0.6) : alpha(theme.palette.text.primary, 0.28),
                                backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.12) : alpha(theme.palette.text.primary, 0.04),
                              },
                              '&.Mui-focusVisible': {
                                outline: `2px solid ${theme.palette.secondary.main}`,
                                outlineOffset: 2,
                              },
                            }}
                          >
                            {isSelected ? (
                              <VideocamRoundedIcon sx={{ fontSize: 17, color: 'secondary.main', flexShrink: 0 }} />
                            ) : (
                              <VideocamOutlinedIcon sx={{ fontSize: 17, color: 'text.disabled', flexShrink: 0 }} />
                            )}
                            <Typography variant="body2" fontWeight={isSelected ? 600 : 500} color="inherit" noWrap sx={{ fontSize: '0.8125rem' }}>
                              {channelLabel(channel)}
                            </Typography>
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  ) : (
                    <Stack
                      alignItems="center"
                      spacing={0.75}
                      sx={{
                        py: 2.5,
                        px: 1.5,
                        borderRadius: '10px',
                        backgroundColor: alpha(theme.palette.text.primary, 0.03),
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {isAdmin && !company?.id && !fixedTruckImplementId
                          ? 'Selecione uma empresa para listar os veículos.'
                          : vehicle
                          ? 'Não há câmeras ativas para este veículo.'
                          : 'Selecione um veículo para listar as câmeras.'}
                      </Typography>
                    </Stack>
                  )}
                </Box>

                {isLiveActive && (
                  <Alert
                    severity="info"
                    variant="outlined"
                    sx={{
                      alignSelf: 'flex-start',
                      display: 'inline-flex',
                      width: 'fit-content',
                      maxWidth: '100%',
                      minHeight: 28,
                      py: 0.25,
                      px: 0.75,
                      borderRadius: '6px',
                      alignItems: 'center',
                      '& .MuiAlert-icon': { fontSize: 16, py: 0, mr: 0.5 },
                      '& .MuiAlert-message': {
                        minWidth: 0,
                        p: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.6875rem',
                        lineHeight: 1.2,
                      },
                    }}
                  >
                    Pare a transmissão para alterar as configurações.
                  </Alert>
                )}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ px: SIDEBAR_PADDING, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
            {startHint && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, textAlign: 'center' }}>
                {startHint}
              </Typography>
            )}
            {!isLiveActive ? (
              <Tooltip title={startHint || ''} disableHoverListener={!startHint} placement="top">
                <Box>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    color="secondary"
                    disableElevation
                    startIcon={loadingLive ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
                    onClick={() => void startLive()}
                    disabled={!canStartLive}
                    sx={{ height: 38, minHeight: 38, px: 1.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
                  >
                    {loadingLive ? 'Iniciando transmissão...' : 'Iniciar transmissão'}
                  </Button>
                </Box>
              </Tooltip>
            ) : (
              <Button
                fullWidth
                size="large"
                variant="contained"
                color="error"
                disableElevation
                startIcon={<StopRoundedIcon />}
                onClick={stopLive}
                sx={{ height: 38, minHeight: 38, px: 1.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
              >
                Parar transmissão
              </Button>
            )}
          </Box>
        </Box>

        <Box
          component="main"
          aria-label="Transmissão ao vivo"
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '100%', md: playerMaxWidth ? `${playerMaxWidth}px` : '100%' },
            maxHeight: { xs: 'none', md: playerMaxHeight ? `${playerMaxHeight}px` : 'none' },
            justifySelf: 'center',
            alignSelf: 'start',
            height: 'auto',
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            overflow: 'visible',
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              width: '100%',
              aspectRatio: '16 / 9',
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              backgroundColor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            {playerStreams.length > 0 ? (
              <Box ref={playerContainerRef} sx={{ width: '100%', height: isPlayerFullscreen ? '100%' : 'auto' }}>
                <HatJsVideoPlayer
                  ref={livePlayerRef}
                  streams={playerStreams}
                  quality={quality}
                  qualityChanging={loadingLive}
                  preserveVideoAspectRatio
                  overlayControls
                  enableSpotlightMode
                  showQualityControl={isAdmin}
                  onQualityChange={changeQuality}
                  title="Monitor ao Vivo"
                  isFullscreen={isPlayerFullscreen}
                  onFullscreenToggle={togglePlayerFullscreen}
                />
              </Box>
            ) : (
              <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', px: 3, py: 4, maxWidth: 380 }}>
                <VideocamOffRoundedIcon sx={{ fontSize: 48, color: onVideoDisabled }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: onVideoPrimary }}>
                  Transmissão não iniciada
                </Typography>
                <Typography variant="body2" sx={{ color: onVideoSecondary }}>
                  Escolha o veículo, selecione as câmeras e inicie a transmissão para visualizar as imagens.
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LiveMonitor;
